import { useEffect, useRef } from 'react'
import { PortalDashboardBody } from './PortalDashboardBody'
import { useQuery } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ApiError, getJSON } from '@/lib/api'
import type { MemberPortalOrganization } from './MemberSignIn'
import { usePortalViewer } from './usePortalViewer'
import { ProfilePanel } from './MemberPortalOrganizationProfile'
import { MemberPortalOrganizationRolesPanel } from './MemberPortalOrganizationRolesPanel'
import { MemberPortalOrganizationMembership } from './MemberPortalOrganizationMembership'
import { MemberPortalPeople } from './MemberPortalPeople'
import { MemberPortalGroups } from './MemberPortalGroups'
import { MemberPortalTransactions } from './MemberPortalTransactions'
import { type PortalDashboardResponse } from './portalDashboard.types'
import {
  usePortalDashboardSelection,
  type PortalOrganizationTab,
} from './usePortalDashboardSelection'

type Selection = ReturnType<typeof usePortalDashboardSelection>

export function MemberPortalOrganizationDashboard({
  organization,
}: {
  organization: MemberPortalOrganization
}) {
  const selection = usePortalDashboardSelection(organization)
  const viewer = usePortalViewer()
  const active = useRef<HTMLButtonElement>(null)
  // The tab row scrolls sideways at 390px; keep the open tab visible.
  useEffect(() => {
    active.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [selection.tab])
  return (
    <Tabs value={selection.tab} onValueChange={selection.selectTab}>
      <TabsList aria-label="Organization sections">
        {selection.tabs.map((item) => (
          <TabsTrigger
            key={item.key}
            value={item.key}
            ref={item.key === selection.tab ? active : undefined}
          >
            {item.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {selection.tabs.map((item) => (
        <TabsContent key={item.key} value={item.key}>
          {selection.tab === item.key && (
            <OrganizationTab
              tab={item.key}
              organization={organization}
              selection={selection}
              viewer={viewer}
            />
          )}
        </TabsContent>
      ))}
    </Tabs>
  )
}

function OrganizationTab({
  tab,
  organization,
  selection,
  viewer,
}: {
  tab: PortalOrganizationTab
  organization: MemberPortalOrganization
  selection: Selection
  viewer: string
}) {
  const key = `${viewer}:${organization.id}:${tab}`
  switch (tab) {
    case 'profile':
      return (
        <Card className="overflow-hidden">
          <ProfilePanel organization={organization} />
        </Card>
      )
    case 'roles':
      return (
        <Card className="overflow-hidden">
          <MemberPortalOrganizationRolesPanel organization={organization} />
        </Card>
      )
    case 'membership':
      return (
        <MemberPortalOrganizationMembership
          key={key}
          organization={organization}
          canManage={selection.canManage}
        />
      )
    case 'people':
      return <MemberPortalPeople key={key} organizationId={organization.id} viewer={viewer} />
    case 'groups':
      return <MemberPortalGroups key={key} organization={organization} viewer={viewer} />
    case 'transactions':
      return <MemberPortalTransactions key={key} organizationId={organization.id} viewer={viewer} />
    default:
      return <DashboardFigures organization={organization} selection={selection} viewer={viewer} />
  }
}

function DashboardFigures({
  organization,
  selection,
  viewer,
}: {
  organization: MemberPortalOrganization
  selection: Selection
  viewer: string
}) {
  const query = useQuery<PortalDashboardResponse>({
    queryKey: ['member-portal-dashboard', viewer, organization.id],
    queryFn: () => getJSON(`/api/v1/member_portal/organizations/${organization.id}/dashboard`),
    retry: false,
  })
  if (query.error instanceof ApiError && query.error.status === 404)
    return <Navigate to="/portal" replace />
  if (query.isPending) return <EmptyState title="Loading dashboard" variant="quiet" />
  if (query.isError)
    return (
      <Alert variant="destructive">
        Dashboard unavailable.{' '}
        <Button variant="outline" size="sm" onClick={() => query.refetch()}>
          Try again
        </Button>
      </Alert>
    )
  return (
    <PortalDashboardBody
      dashboard={query.data.dashboard}
      selection={selection}
      organization={organization}
      viewer={viewer}
    />
  )
}
